export type LayoutType = 'FILL' | 'TILES' | 'SHARED_FILL' | 'FILM_STRIP' | 'PIP' | 'ONLY_SCREEN';

export type PipShape = 'CIRCULAR' | 'RECTANGULAR';

export type SpeakerSide = 'LEFT' | 'RIGHT';

export interface LayoutParams {
  /** Layout type */
  layout: LayoutType;
  /** Height / width ratio of the container (e.g. 0.5625 for 16:9, >1 for portrait). */
  aspectRatio: number;
  /** Number of speakers (excludes screen share) */
  speakerCount: number;
  /** Whether to apply rounded border radius */
  border: boolean;
  /** For SHARED_FILL / FILM_STRIP: which side the speakers are on */
  speakerSide?: SpeakerSide;
  /** For PIP: shape of the overlay */
  pipShape?: PipShape;
  /** When false, TILES tiles stretch to fill the container height (no AR cap). */
  keepAspectRatio?: boolean;
}

export interface TileStyle {
  style: {
    objectFit: 'cover';
    width: '100%';
    height: '100%';
  };
  containerStyle: {
    overflow: 'hidden';
    borderRadius: string;
    position: 'absolute';
    top: string;
    left: string;
    width: string;
    height: string;
    [key: string]: any;
  };
}

export interface LayoutResult {
  /** For screen-share layouts: element 0 = screen, rest = speakers.
   *  For speaker-only layouts: all elements are speakers. */
  tiles: TileStyle[];
}

// ═══════════════════════════════════════════════════════════════════════════
// Internal helpers
// ═══════════════════════════════════════════════════════════════════════════

const COVER_FIT = { objectFit: 'cover' as const, width: '100%' as const, height: '100%' as const };

const BORDER_RADIUS_8 = '8px';
const BORDER_RADIUS_12 = '12px';
const NO_RADIUS = '0px';

/** Target tile width:height ratio for TILES layout.
 *  Matches 16:9 webcam feeds so `objectFit: cover` crops minimally.
 *  Row heights scale proportionally — wider rows become taller
 *  so every tile shares the same effective AR. */
const TARGET_TILE_AR = 16 / 9;

/** In SHARED_FILL the speaker panel is narrow so 16:9 tiles end up tiny.
 *  4:3 fills the vertical space much better and still looks natural for
 *  webcam thumbnails. */
const SHARED_FILL_TILE_AR = 4 / 2;

/** Create a tile using container query units (cqw / cqh).
 *  Requires `container-type: size` on the nearest stageContent ancestor. */
function tile(
  topPx: number,
  leftPx: number,
  widthPx: number,
  heightPx: number,
  containerW: number,
  containerH: number,
  radius: string,
  extra: Record<string, any> = {}
): TileStyle {
  const cqw = (v: number) => `${(v / containerW) * 100}cqw`;
  const cqh = (v: number) => `${(v / containerH) * 100}cqh`;
  return {
    style: { ...COVER_FIT },
    containerStyle: {
      overflow: 'hidden' as const,
      borderRadius: radius,
      position: 'absolute' as const,
      top: cqh(topPx),
      left: cqw(leftPx),
      width: cqw(widthPx),
      height: cqh(heightPx),
      ...extra
    }
  };
}

/** Create a tile using raw string values (for PIP special positioning). */
function tileRaw(
  top: string,
  left: string,
  width: string,
  height: string,
  radius: string,
  extra: Record<string, any> = {}
): TileStyle {
  return {
    style: { ...COVER_FIT },
    containerStyle: {
      overflow: 'hidden' as const,
      borderRadius: radius,
      position: 'absolute' as const,
      top,
      left,
      width,
      height,
      ...extra
    }
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Grid helpers (shared across multiple layout computers)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Returns info about the last ("remainder") row when it has fewer columns
 * than the row above it — e.g. [2,1] for 3 speakers.
 * Remainder tiles are sized to match the preceding row and are centred.
 */
function incompleteRow(dist: number[], gap: number, usableW: number) {
  const lastIdx = dist.length - 1;
  const lastCols = dist[lastIdx];
  const prevCols = dist.length > 1 ? dist[lastIdx - 1] : lastCols;
  const isIncomplete = lastCols < prevCols;
  // Size remainder tiles as if the fuller preceding row continued
  const tileW = isIncomplete ? (usableW - (prevCols - 1) * gap) / prevCols : 0;
  return { lastIdx, isIncomplete, tileW };
}

/**
 * Renders a grid of speaker tiles into absolute-positioned TileStyle objects.
 * Shared by computeTiles and computePortraitScreenShare.
 *
 * @param dist       Columns-per-row distribution (e.g. [3, 3] for 6 speakers)
 * @param gap        Pixel gap between tiles (both axes)
 * @param usableW    Available grid width in pixels
 * @param containerH Available grid height in pixels
 * @param baseY      Top of the grid area in stage pixels (before centering)
 * @param margin     Left inset in stage pixels
 * @param W / H      Full stage dimensions (for % conversion)
 * @param radius     CSS border-radius string
 * @param scaleCap   Max scale factor: 1 = natural 16:9; TARGET_TILE_AR = fill landscape stage
 */
function buildSpeakerGrid(
  dist: number[],
  gap: number,
  usableW: number,
  containerH: number,
  baseY: number,
  margin: number,
  W: number,
  H: number,
  radius: string,
  scaleCap: number
): TileStyle[] {
  const rowCount = dist.length;
  const vGaps = (rowCount - 1) * gap;
  const { lastIdx, isIncomplete: lastIsIncomplete, tileW: incompleteTileW } = incompleteRow(dist, gap, usableW);

  const naturalHeights = dist.map((cols, i) => {
    const tw = i === lastIdx && lastIsIncomplete ? incompleteTileW : (usableW - (cols - 1) * gap) / cols;
    return tw / TARGET_TILE_AR;
  });

  const sumH = naturalHeights.reduce((a, b) => a + b, 0);
  const scale = Math.min(scaleCap, (containerH - vGaps) / sumH);
  const totalGridH = sumH * scale + vGaps;

  let topOffset = baseY + Math.max(0, (containerH - totalGridH) / 2);
  const tiles: TileStyle[] = [];

  for (let r = 0; r < rowCount; r++) {
    const cols = dist[r];
    const incomplete = r === lastIdx && lastIsIncomplete;
    const cellW = incomplete ? incompleteTileW : (usableW - (cols - 1) * gap) / cols;
    const cellH = naturalHeights[r] * scale;
    const rowW = cols * cellW + (cols - 1) * gap;
    const leftOffset = incomplete ? margin + (usableW - rowW) / 2 : margin;

    for (let c = 0; c < cols; c++) {
      tiles.push(tile(topOffset, leftOffset + c * (cellW + gap), cellW, cellH, W, H, radius));
    }
    topOffset += cellH + gap;
  }

  return tiles;
}

// ═══════════════════════════════════════════════════════════════════════════
// Distribution tables
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Row distribution for FILL / TILES layouts.
 * Key = speaker count, Value = array of "speakers per row" (top to bottom).
 *
 * Pattern: 1→[1], 2→[2], 3→[2,1], 4→[2,2], 5→[2,3], 6→[3,3], 7→[3,4], 8→[4,4]
 */
const GRID_DISTRIBUTION: Record<number, number[]> = {
  1: [1],
  2: [2],
  3: [2, 1],
  4: [2, 2],
  5: [2, 3],
  6: [3, 3],
  7: [3, 4],
  8: [2, 3, 3],
  9: [3, 3, 3],
  10: [3, 3, 4],
  11: [3, 4, 4],
  12: [3, 4, 5],
  13: [3, 5, 5],
  14: [3, 3, 4, 4],
  15: [3, 3, 4, 5],
  16: [3, 3, 5, 5],
  17: [3, 4, 5, 5],
  18: [3, 5, 5, 5],
  19: [4, 5, 5, 5],
  20: [5, 5, 5, 5],
  21: [4, 4, 4, 4, 5],
  22: [4, 4, 4, 5, 5],
  23: [4, 4, 5, 5, 5],
  24: [4, 5, 5, 5, 5]
};

/**
 * Portrait-optimised variant of GRID_DISTRIBUTION.
 * Fewer columns per row so tiles stay close to 16:9 on tall/narrow stages
 * (e.g. mobile portrait).  Only entries that differ from the landscape
 * table are listed; the rest fall back to GRID_DISTRIBUTION at runtime.
 */
const GRID_DISTRIBUTION_PORTRAIT: Record<number, number[]> = {
  ...GRID_DISTRIBUTION,
  // 2 speakers: stacked 1×1 rows fill portrait better than side-by-side
  2: [1, 1],
  // 6 speakers: 3 rows × 2 cols → 16:9 tiles; [3,3] would need ×1.78 scale → square
  6: [2, 2, 2],
  // 7 speakers: portrait-friendly 3-row arrangement
  7: [2, 2, 3]
};

/**
 * Row distribution for SHARED_FILL speaker panel.
 * Each row entry: [colsInRow]. A row marked with `fw: true` means a
 * single speaker spans the full panel width (rendered as 1 column).
 *
 * Pattern:
 *   1 → 1 speaker (full-width)
 *   2 → 2 rows × 1 col
 *   3 → 1 full-width + 1 row × 2 cols
 *   4 → 2 rows × 2 cols
 *   5 → 1 full-width + 2 rows × 2 cols
 *   6 → 3 rows × 2 cols
 *   7 → 1 full-width + 3 rows × 2 cols
 *   8 → 4 rows × 2 cols
 */
interface SharedFillRow {
  cols: number;
  /** If true, this row has a single speaker spanning the full panel width */
  fullWidth?: boolean;
  scaleRatio?: number;
}

const SHARED_FILL_DISTRIBUTION: Record<number, SharedFillRow[]> = {
  1: [{ cols: 1, fullWidth: true, scaleRatio: 2.6 }],
  2: [
    { cols: 1, scaleRatio: 1.4 },
    { cols: 1, scaleRatio: 1.4 }
  ],
  3: [
    { cols: 1, fullWidth: true, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.8 }
  ],
  4: [
    { cols: 2, scaleRatio: 2 },
    { cols: 2, scaleRatio: 2 }
  ],
  5: [
    { cols: 1, fullWidth: true, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.5 },
    { cols: 2, scaleRatio: 1.5 }
  ],
  6: [
    { cols: 2, scaleRatio: 1.8 },
    { cols: 2, scaleRatio: 1.8 },
    { cols: 2, scaleRatio: 1.8 }
  ],
  7: [
    { cols: 1, fullWidth: true, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.4 }
  ],
  8: [
    { cols: 2, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.4 }
  ],
  9: [{ cols: 2 }, { cols: 2 }, { cols: 2 }, { cols: 3 }],
  10: [{ cols: 2 }, { cols: 2 }, { cols: 3 }, { cols: 3 }],
  11: [{ cols: 2 }, { cols: 3 }, { cols: 3 }, { cols: 3 }],
  12: [{ cols: 3 }, { cols: 3 }, { cols: 3 }, { cols: 3 }],
  13: [{ cols: 3 }, { cols: 3 }, { cols: 3 }, { cols: 3 }, { cols: 1 }],
  14: [{ cols: 3 }, { cols: 3 }, { cols: 3 }, { cols: 3 }, { cols: 2 }],
  15: [{ cols: 3 }, { cols: 3 }, { cols: 3 }, { cols: 3 }, { cols: 3 }],
  16: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }],
  17: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 1 }],
  18: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 2 }],
  19: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 3 }],
  20: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }],
  21: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 1 }],
  22: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 2 }],
  23: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 3 }],
  24: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }]
};

// ═══════════════════════════════════════════════════════════════════════════
// Layout computers
// ═══════════════════════════════════════════════════════════════════════════

/**
 * FILL – edge-to-edge, no gaps, no border radius.
 *
 * Row heights are derived from tile width × 16:9 AR so every row is
 * proportionally correct.  The grid is scaled to exactly fill the
 * container height.  Incomplete rows use the max-cols tile width so
 * their tiles are the same size as their neighbours, then centred.
 */
function computeFill(W: number, H: number, count: number): TileStyle[] {
  const dist = GRID_DISTRIBUTION[Math.min(count, 24)] || GRID_DISTRIBUTION[24];
  // FILL has no gap, so incompleteTileW = W / precedingCols
  const { lastIdx, isIncomplete: lastIsIncomplete, tileW: incompleteTileW } = incompleteRow(dist, 0, W);

  // Natural (AR-preserving) row height = tileWidth / 16:9
  const naturalRowH = dist.map((cols, i) => {
    const tw = i === lastIdx && lastIsIncomplete ? incompleteTileW : W / cols;
    return tw / TARGET_TILE_AR;
  });

  // Scale so all rows together fill exactly H
  const scale = H / naturalRowH.reduce((a, b) => a + b, 0);

  const tiles: TileStyle[] = [];
  let topOffset = 0;

  for (let r = 0; r < dist.length; r++) {
    const cols = dist[r];
    const incomplete = r === lastIdx && lastIsIncomplete;
    const cellW = incomplete ? incompleteTileW : W / cols;
    const cellH = naturalRowH[r] * scale;
    const leftStart = incomplete ? (W - cols * cellW) / 2 : 0;

    for (let c = 0; c < cols; c++) {
      tiles.push(tile(topOffset, leftStart + c * cellW, cellW, cellH, W, H, NO_RADIUS));
    }
    topOffset += cellH;
  }

  return tiles;
}

/**
 * TILES – all speakers with gaps and rounded corners.
 *
 * Each row's height is derived from its tile width and TARGET_TILE_AR so
 * every tile shares the same display aspect ratio.  When the AR-based grid
 * is taller than the container the heights are scaled down proportionally
 * (tiles get slightly flatter but stay uniform).  When shorter, the grid
 * is centred vertically.
 */
function computeTiles(W: number, H: number, count: number, border: boolean, keepAspectRatio: boolean): TileStyle[] {
  const margin = W * 0.011;
  const gap = W * 0.012;
  const radius = border ? BORDER_RADIUS_12 : NO_RADIUS;

  // Portrait (H ≥ W): use fewer columns so tiles stay closer to 16:9.
  const isPortrait = H >= W;
  const distTable = isPortrait ? GRID_DISTRIBUTION_PORTRAIT : GRID_DISTRIBUTION;
  const dist = distTable[Math.min(count, 24)] || distTable[24];
  // When keepAspectRatio is false, tiles fill the full container height.
  // When true, scale is capped at 1 so tiles never exceed their natural 16:9 aspect ratio.
  const scaleCap = keepAspectRatio ? 1 : Infinity;

  return buildSpeakerGrid(
    dist,
    gap,
    W - 2 * margin, // usableW
    H - 2 * margin, // containerH
    margin, // baseY
    margin, // left margin
    W,
    H,
    radius,
    scaleCap
  );
}

/**
 * Portrait screen-share layout (used by SHARED_FILL and FILM_STRIP when H ≥ W).
 *
 * On portrait containers (mobile phones, narrow windows) a left/right split
 * produces an unusably narrow speaker panel.  Instead: screen share takes the
 * top ~42 % of height and the speaker grid fills the remaining space below.
 * Both SHARED_FILL and FILM_STRIP render identically on portrait — the
 * left/right "side" setting is irrelevant and ignored.
 *
 * Tile AR: capped at 4:3 (SHARED_FILL_TILE_AR) so that even on dense grids
 * tiles remain wider-than-tall rather than flipping to square/portrait.
 */
function computePortraitScreenShare(W: number, H: number, count: number, border: boolean): TileStyle[] {
  const radius = border ? BORDER_RADIUS_8 : NO_RADIUS;

  const margin = W * 0.011;
  const sectionGap = W * 0.012;
  const tileGap = sectionGap;
  const usableW = W - 2 * margin;
  const usableH = H - 2 * margin;

  // Screen share: full width, top 42% of usable height
  const screenH = Math.round(usableH * 0.42);
  const tiles: TileStyle[] = [tile(margin, margin, usableW, screenH, W, H, radius)];

  const speakerAreaTop = margin + screenH + sectionGap;
  const speakerAreaH = usableH - screenH - sectionGap;
  if (speakerAreaH <= 0 || count === 0) {
    return tiles;
  }

  const dist = GRID_DISTRIBUTION_PORTRAIT[Math.min(count, 24)] || GRID_DISTRIBUTION_PORTRAIT[24];

  // Cap at 4:3 so speaker tiles remain wider-than-tall on dense portrait grids
  return [
    ...tiles,
    ...buildSpeakerGrid(dist, tileGap, usableW, speakerAreaH, speakerAreaTop, margin, W, H, radius, SHARED_FILL_TILE_AR)
  ];
}

/**
 * SHARED_FILL – split view with screen share on one side, speakers on the other.
 *
 * Speaker panel takes ~32% of width, screen takes ~66%.
 * Gap between screen and speakers panel = 1.5%.
 *
 * Each speaker tile's width is derived from the column count of its row,
 * and tile height = width / TARGET_TILE_AR (matching 16:9 webcam feeds).
 * When tiles don't fit vertically they are scaled down uniformly.
 */
function computeSharedFill(W: number, H: number, count: number, border: boolean, side: SpeakerSide): TileStyle[] {
  // On portrait containers use a stacked layout instead of left/right split
  if (H >= W) {
    return computePortraitScreenShare(W, H, count, border);
  }

  const radius = border ? BORDER_RADIUS_8 : NO_RADIUS;
  const margin = W * 0.011;
  const panelGap = W * 0.012;
  const tileGap = W * 0.01;

  const usableW = W - 2 * margin;
  const usableH = H - 2 * margin;

  // For 1 speakers use a narrower panel so the screen share dominates
  const panelRatio = count < 2 ? 0.24 : 0.32;
  const speakerPanelW = usableW * panelRatio;
  const screenW = usableW - speakerPanelW - panelGap;

  const isLeft = side === 'LEFT';
  const speakerLeft = isLeft ? margin : margin + screenW + panelGap;
  const screenLeft = isLeft ? margin + speakerPanelW + panelGap : margin;

  const tiles: TileStyle[] = [];

  const screenH = usableH * 0.75;
  const screenTop = margin + (usableH - screenH) / 2;
  tiles.push(tile(screenTop, screenLeft, screenW, screenH, W, H, radius));

  // --- Speaker tiles ---
  const dist = SHARED_FILL_DISTRIBUTION[Math.min(count, 24)] || SHARED_FILL_DISTRIBUTION[24];
  const rowCount = dist.length;
  const vGaps = (rowCount - 1) * tileGap;

  /** Width of a single tile in a row with `cols` columns. */
  const colWidth = (cols: number): number => (speakerPanelW - (cols - 1) * tileGap) / cols;

  /** Tile width for a row. fullWidth rows span the panel.
   *  Remainder rows (last row with fewer cols) inherit the previous row's tile width
   *  so leftover speakers stay the same size as the rest of the grid. */
  const tileWidthForRow = (row: SharedFillRow, idx: number): number => {
    if (row.fullWidth) {
      return speakerPanelW;
    }
    // Last row with fewer cols than the preceding row — inherit preceding width
    if (idx > 0 && idx === rowCount - 1) {
      const prev = dist[idx - 1];
      if (!prev.fullWidth && row.cols < prev.cols) {
        return colWidth(prev.cols);
      }
    }
    return colWidth(row.cols);
  };

  // Compute each row's height using its per-row scaleRatio.
  const availableH = usableH - vGaps;
  const rowHeights = dist.map((row, i) => {
    const naturalH = tileWidthForRow(row, i) / SHARED_FILL_TILE_AR;
    return naturalH * (row.scaleRatio ?? 1.7);
  });

  // If total exceeds available space, scale everything down uniformly to fit.
  const totalDesired = rowHeights.reduce((a, b) => a + b, 0);
  const fitScale = totalDesired > availableH ? availableH / totalDesired : 1;
  const finalRowHeights = rowHeights.map(h => h * fitScale);

  // Center the grid vertically
  const totalGridH = finalRowHeights.reduce((a, b) => a + b, 0) + vGaps;
  let topOffset = margin + Math.max(0, (usableH - totalGridH) / 2);

  for (let r = 0; r < rowCount; r++) {
    const row = dist[r];
    const cols = row.cols;
    const tw = tileWidthForRow(row, r);
    const th = finalRowHeights[r];

    if (row.fullWidth) {
      tiles.push(tile(topOffset, speakerLeft, tw, th, W, H, radius));
    } else if (cols < (dist[Math.min(r + 1, rowCount - 1)]?.cols ?? cols)) {
      // Row with fewer tiles than the next — centre in the panel
      const rowW = cols * tw + (cols - 1) * tileGap;
      const rowLeft = speakerLeft + (speakerPanelW - rowW) / 2;
      for (let c = 0; c < cols; c++) {
        tiles.push(tile(topOffset, rowLeft + c * (tw + tileGap), tw, th, W, H, radius));
      }
    } else if (r === rowCount - 1 && r > 0 && cols < dist[r - 1].cols) {
      // Remainder row — centre the tiles in the panel
      const rowW = cols * tw + (cols - 1) * tileGap;
      const rowLeft = speakerLeft + (speakerPanelW - rowW) / 2;
      for (let c = 0; c < cols; c++) {
        tiles.push(tile(topOffset, rowLeft + c * (tw + tileGap), tw, th, W, H, radius));
      }
    } else {
      for (let c = 0; c < cols; c++) {
        tiles.push(tile(topOffset, speakerLeft + c * (tw + tileGap), tw, th, W, H, radius));
      }
    }

    topOffset += th + tileGap;
  }

  return tiles;
}

/**
 * FILM_STRIP – large screen share with a speaker strip.
 *
 * Always single column up to 12 speakers. Tile height is derived by dividing
 * usableH equally across all speakers including gaps — no overflow, no scroll.
 *
 * Tile aspect ratio is 4:3 (landscape) so faces are wider and more visible
 * even at high speaker counts where height is constrained. Strip width =
 * tile width and is capped at 22 % of usableW so the screen share always
 * dominates.
 *
 * All tile coordinates use cqw / cqh so they scale to any container size.
 */
function computeFilmStrip(W: number, H: number, count: number, border: boolean, side: SpeakerSide): TileStyle[] {
  // On portrait containers use the same stacked layout as SHARED_FILL
  if (H >= W) {
    return computePortraitScreenShare(W, H, count, border);
  }

  const radius = border ? BORDER_RADIUS_8 : NO_RADIUS;
  const margin = W * 0.011;
  const stripGap = W * 0.012;
  const tileGap = W * Math.min(0.048 / count, 0.012);

  const usableW = W - 2 * margin;
  const usableH = H - 2 * margin;

  // Single column always. Distribute usableH equally — no overflow, no scroll.
  const vGaps = Math.max(count - 1, 0) * tileGap;
  const maxTileH = (usableH - vGaps) / count;

  // 4:3 landscape aspect — 33% wider than square, shows more face at high counts.
  // Cap at 22% of usableW so screen share always keeps at least ~76% of the width.
  const tileW = Math.min(maxTileH * (4 / 3), usableW * 0.22);

  // Enforce aspect ratio: if tileW was capped, shrink tileH to match 4:3.
  const tileH = Math.min(maxTileH, tileW / (4 / 3));
  const stripW = tileW;
  const screenW = usableW - stripW - stripGap;

  // Center the strip vertically when tiles don't fill the full height
  const totalStripH = count * tileH + vGaps;
  const stripTopOffset = margin + (usableH - totalStripH) / 2;

  const isLeft = side === 'LEFT';
  const stripLeftPx = isLeft ? margin : margin + screenW + stripGap;
  const screenLeft = isLeft ? margin + stripW + stripGap : margin;

  const tiles: TileStyle[] = [];

  // Tile 0: Screen share – fills full usable height
  tiles.push(tile(margin, screenLeft, screenW, usableH, W, H, radius));

  // Speaker tiles: single column, vertically centered, respecting 4:3 aspect ratio
  for (let i = 0; i < count; i++) {
    const topPx = stripTopOffset + i * (tileH + tileGap);
    tiles.push(tile(topPx, stripLeftPx, tileW, tileH, W, H, radius));
  }

  return tiles;
}

/**
 * PIP – full-screen screen share with a small speaker overlay.
 * The PiP position is controlled separately via PIP_POSITION_STYLES.
 */
function computePip(W: number, H: number, _count: number, border: boolean, pipShape: PipShape): TileStyle[] {
  const radius = border ? BORDER_RADIUS_8 : NO_RADIUS;
  const margin = border ? W * 0.00833 : 0;

  const usableW = W - 2 * margin;
  const usableH = H - 2 * margin;

  const tiles: TileStyle[] = [];

  // Element 0: Screen share (full container)
  tiles.push(tile(margin, margin, usableW, usableH, W, H, radius));

  // Element 1: PiP speaker overlay
  // Position is handled by `pipOverlayStyle` in VideoLayout.vue — we only define size here
  if (pipShape === 'CIRCULAR') {
    // Circular: 20% width, square aspect ratio, border-radius 100%
    tiles.push(
      tileRaw('auto', 'auto', '20%', 'auto', '100%', { zIndex: 10, aspectRatio: '1', bottom: '2.5%', right: '2%' })
    );
  } else {
    // Rectangular: 22% × 22% of container
    const pipW = usableW * 0.22;
    const pipH = usableH * 0.22;
    const pipTop = margin + usableH - pipH - H * 0.025;
    const pipLeft = margin + usableW - pipW - W * 0.02;
    tiles.push(tile(pipTop, pipLeft, pipW, pipH, W, H, radius, { zIndex: 10 }));
  }

  return tiles;
}

/**
 * ONLY_SCREEN – screen share takes the entire stage.
 */
function computeOnlyScreen(W: number, H: number, _count: number, border: boolean): TileStyle[] {
  const radius = border ? BORDER_RADIUS_8 : NO_RADIUS;
  const margin = border ? W * 0.00833 : 0;
  const usableW = W - 2 * margin;
  const usableH = H - 2 * margin;

  return [tile(margin, margin, usableW, usableH, W, H, radius)];
}

// ═══════════════════════════════════════════════════════════════════════════
// Public API
// ═══════════════════════════════════════════════════════════════════════════

export function computeLayout(params: LayoutParams): LayoutResult {
  const {
    layout,
    aspectRatio,
    speakerCount,
    border,
    speakerSide = 'LEFT',
    pipShape = 'RECTANGULAR',
    keepAspectRatio = true
  } = params;

  if (aspectRatio <= 0) {
    return { tiles: [] };
  }

  // Normalised coordinate space: W is always 100, H scales with aspect ratio.
  // tile() emits cqw/cqh so the browser resolves them against the real container.
  const W = 100;
  const H = aspectRatio * 100;
  const count = Math.max(1, Math.min(speakerCount, 24));

  switch (layout) {
    case 'FILL':
      return { tiles: computeFill(W, H, count) };

    case 'TILES':
      return { tiles: computeTiles(W, H, count, border, keepAspectRatio) };

    case 'SHARED_FILL':
      return { tiles: computeSharedFill(W, H, count, border, speakerSide) };

    case 'FILM_STRIP':
      return { tiles: computeFilmStrip(W, H, count, border, speakerSide) };

    case 'PIP':
      return { tiles: computePip(W, H, count, border, pipShape) };

    case 'ONLY_SCREEN':
      return { tiles: computeOnlyScreen(W, H, count, border) };

    default:
      return { tiles: [] };
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Distribution helpers (exported for unit testing / React reuse)
// ═══════════════════════════════════════════════════════════════════════════

export { GRID_DISTRIBUTION, SHARED_FILL_DISTRIBUTION };
export type { SharedFillRow };
